/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [inbox, setInbox] = useState([]);
  const [metric, setMetric] = useState("users_by_tier");
  const [adhoc, setAdhoc] = useState([]);

  const load = useCallback(async () => {
    try {
      const [s, u, r, i] = await Promise.all([
        api.get("/admin/analytics"), api.get("/admin/users"), api.get("/admin/reports"), api.get("/admin/inbox")
      ]);
      setStats(s.data); setUsers(u.data); setReports(r.data); setInbox(i.data);
    } catch (err) {
      console.error("Admin load failed:", err);
      toast.error("Admin load failed.");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const pendingInbox = useMemo(() => inbox.filter(i => i.status === "pending"), [inbox]);

  const statCards = stats ? [
    { l: "Total members", v: stats.total_users },
    { l: "Open reports", v: stats.open_reports },
    { l: "Pending ID verifications", v: stats.pending_id_verifications },
    { l: "Receipts", v: stats.total_receipts },
  ] : [];

  const runAdHoc = async () => {
    const { data } = await api.post("/admin/adhoc", { metric });
    setAdhoc(data);
  };
  const verify = async (uid) => { await api.post(`/admin/verify/${uid}`); toast.success("Verified."); load(); };
  const ban = async (uid) => { await api.post(`/admin/ban/${uid}`); toast.success("User banned."); load(); };

  return (
    <div className="min-h-screen p-6" style={{ background: "var(--lavender)" }}>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-display text-4xl text-white font-extrabold">Admin · Moderator</h1>
          <Link to="/app"><Button variant="ghost" className="text-white hover:bg-white/10" data-testid="admin-to-app">Back to app</Button></Link>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {statCards.map((c) => (
            <div key={c.l} className="glass rounded-2xl p-5" data-testid={`stat-${c.l.toLowerCase().replace(/\s+/g, '-')}`}>
              <p className="text-white/70 text-sm">{c.l}</p>
              <p className="font-display text-3xl text-white font-bold mt-1">{c.v}</p>
            </div>
          ))}
        </div>

        {stats && (
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            <div className="glass rounded-2xl p-5">
              <h3 className="font-display text-xl text-white font-bold mb-3">Members by tier</h3>
              <div className="space-y-2">{Object.entries(stats.by_tier).map(([k,v]) => <div key={k} className="flex justify-between text-white"><span className="capitalize">{k}</span><strong>{v}</strong></div>)}</div>
            </div>
            <div className="glass rounded-2xl p-5">
              <h3 className="font-display text-xl text-white font-bold mb-3">Top locations</h3>
              <div className="space-y-2">{stats.top_locations.map((l) => <div key={l.location || "_unknown"} className="flex justify-between text-white"><span>{l.location || "—"}</span><strong>{l.count}</strong></div>)}</div>
            </div>
          </div>
        )}

        <div className="glass rounded-2xl p-5 mb-8">
          <h3 className="font-display text-xl text-white font-bold mb-3">Ad-hoc report</h3>
          <div className="flex flex-wrap gap-3 items-end">
            <Select value={metric} onValueChange={setMetric}>
              <SelectTrigger data-testid="adhoc-metric" className="w-64 bg-white/10 border-white/20 text-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="users_by_tier">Users by tier</SelectItem>
                <SelectItem value="users_by_location">Users by location</SelectItem>
                <SelectItem value="users_by_orientation">Users by orientation</SelectItem>
                <SelectItem value="signups_by_day">Signups by day</SelectItem>
                <SelectItem value="reports_by_reason">Reports by reason</SelectItem>
              </SelectContent>
            </Select>
            <Button data-testid="adhoc-run" onClick={runAdHoc} className="bg-[#FF1493] hover:bg-[#d1107a]">Run</Button>
          </div>
          {adhoc.length > 0 && <div className="mt-4 space-y-1">{adhoc.map((r) => <div key={String(r.key)} className="flex justify-between text-white border-b border-white/10 py-1"><span>{String(r.key)}</span><strong>{r.count}</strong></div>)}</div>}
        </div>

        <div className="glass rounded-2xl p-5 mb-8">
          <h3 className="font-display text-xl text-white font-bold mb-3">Pending ID verifications</h3>
          {pendingInbox.length === 0 ? <p className="text-white/70">None pending.</p> : (
            <div className="space-y-2">{pendingInbox.map((m) => (
              <div key={m._id || m.user_id} className="flex justify-between items-center bg-white/5 rounded-lg p-3 text-white" data-testid={`pending-${m.user_id}`}>
                <div><p className="font-bold">{m.user_email}</p><p className="text-white/70 text-xs">{m.tier} · {m.ts}</p></div>
                <Button size="sm" data-testid={`verify-${m.user_id}`} onClick={() => verify(m.user_id)} className="bg-[#00FFFF] text-black hover:bg-[#00cccc]">Approve</Button>
              </div>
            ))}</div>
          )}
        </div>

        <div className="glass rounded-2xl p-5 mb-8">
          <h3 className="font-display text-xl text-white font-bold mb-3">Recent reports</h3>
          {reports.length === 0 ? <p className="text-white/70">No reports.</p> : (
            <div className="space-y-2 text-white">{reports.slice(0, 20).map((r) => (
              <div key={r._id} className="bg-white/5 rounded-lg p-3"><p><strong>{r.reason}</strong></p><p className="text-white/70 text-xs">Reporter: {r.reporter_id} → Reported: {r.reported_user_id}</p>{r.notes && <p className="text-white/85 text-sm mt-1">"{r.notes}"</p>}</div>
            ))}</div>
          )}
        </div>

        <div className="glass rounded-2xl p-5">
          <h3 className="font-display text-xl text-white font-bold mb-3">Users ({users.length})</h3>
          <div className="overflow-x-auto"><table className="w-full text-white text-sm" data-testid="admin-users-table">
            <thead><tr className="text-left text-white/70 border-b border-white/10"><th className="py-2">Name</th><th>Email</th><th>Tier</th><th>Location</th><th>Verified</th><th></th></tr></thead>
            <tbody>{users.map(u => (
              <tr key={u.id} className="border-b border-white/5">
                <td className="py-2">{u.name}</td><td>{u.email}</td><td className="capitalize">{u.tier}</td><td>{u.location || "—"}</td><td>{u.id_verified ? "✓" : "—"}</td>
                <td className="text-right">{u.role !== "admin" && <Button size="sm" variant="destructive" data-testid={`ban-${u.id}`} onClick={() => ban(u.id)}>Ban</Button>}</td>
              </tr>
            ))}</tbody>
          </table></div>
        </div>
      </div>
    </div>
  );
}
